import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { packageApi } from "../../../api/packageApi";
import { useNotifications } from "../../../context/NotificationContext";
import "./PackageSelection.css";

const PackageSelection = () => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { socket } = useNotifications();

  const fetchPackages = useCallback(async () => {
    try {
      const response = await packageApi.getPackages();
      const availablePackages = response.data || [];
      setPackages(availablePackages);
      setSelectedPackage((currentSelection) =>
        availablePackages.some(
          (item) => item.PackageID === currentSelection,
        )
          ? currentSelection
          : availablePackages[0]?.PackageID || null,
      );
    } catch (error) {
      setError("Failed to load packages");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
    if (!socket) return undefined;
    socket.on("platform:updated", fetchPackages);
    return () => socket.off("platform:updated", fetchPackages);
  }, [fetchPackages, socket]);

  const handleSelect = (packageId) => {
    setSelectedPackage(packageId);
    navigate("/request-store", { state: { selectedPackage: packageId } });
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading packages...</p>
      </div>
    );
  }

  return (
    <div className="package-selection-page">
      <h2 style={{ marginBottom: "8px" }}>Choose Your Package</h2>
      <p style={{ color: "var(--text-light)", marginBottom: "24px" }}>
        Select the package that best fits your business needs
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      <div
        className="package-selection-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "24px",
          marginBottom: "32px",
        }}
      >
        {packages.map((pkg) => (
          <div
            key={pkg.PackageID}
            className={`package-selection-card ${
              selectedPackage === pkg.PackageID ? "is-selected" : ""
            }`}
            onClick={() => handleSelect(pkg.PackageID)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleSelect(pkg.PackageID);
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              padding: "24px",
              border:
                selectedPackage === pkg.PackageID
                  ? "3px solid var(--primary)"
                  : "2px solid #e0e0e0",
              borderRadius: "12px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              background:
                selectedPackage === pkg.PackageID ? "#f5f7fa" : "white",
              boxShadow:
                selectedPackage === pkg.PackageID
                  ? "var(--shadow-md)"
                  : "var(--shadow-sm)",
            }}
          >
            <h3
              style={{
                color: "var(--primary)",
                fontSize: "20px",
                marginBottom: "4px",
              }}
            >
              {pkg.Name}
            </h3>
            <p
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: "var(--primary)",
                marginBottom: "4px",
              }}
            >
              R{pkg.Price}
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "400",
                  color: "var(--text-light)",
                }}
              >
                /{pkg.BillingPeriod}
              </span>
            </p>
            {pkg.SetupFee > 0 && (
              <p style={{ fontSize: "14px", color: "var(--text-light)" }}>
                Setup fee: R{pkg.SetupFee}
              </p>
            )}
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                marginTop: "16px",
                fontSize: "14px",
              }}
            >
              <li style={{ padding: "4px 0" }}>
                📦 {pkg.ProductLimit || "Unlimited"} products
              </li>
              {pkg.Features &&
                Object.entries(pkg.Features).map(([key, value]) => (
                  <li key={key} style={{ padding: "4px 0" }}>
                    ✅ {key.replace(/_/g, " ")}:{" "}
                    {typeof value === "boolean"
                      ? value
                        ? "Yes"
                        : "No"
                      : value}
                  </li>
                ))}
            </ul>
            {selectedPackage === pkg.PackageID && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "8px",
                  background: "var(--primary)",
                  color: "white",
                  borderRadius: "4px",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                Selected ✓
              </div>
            )}
          </div>
        ))}
      </div>

    </div>
  );
};

export default PackageSelection;
